import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type ManagementTaskType =
  | "清掃"
  | "点検"
  | "検査"
  | "調査"
  | "業務"
  | "試験"
  | "その他";

export type ManagementTaskFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annual"
  | "always";

export type ManagementTaskFrequencyUnit = "週" | "月" | "年";

export type ManagementTaskTargetArea =
  | "専有部"
  | "共用部"
  | "建物設備"
  | "敷地"
  | "その他";

export interface LinkedUnit {
  unit_id: string;
  area_type: import("@ippon/shared-types").AreaType;
  name: string;
  subtype_label?: string;
}

export interface ManagementTaskAttachment {
  attachment_id: string;
  file_name: string;
  s3_key: string;
  uploaded_at: string;
}

export interface ManagementTaskCustomFieldValue {
  field_id: string;
  value: string | number | boolean | string[] | null;
}

export interface ManagementTaskRecord {
  client_id: string;
  sort_key: string;
  task_id: string;
  property_id: string;

  no: number;
  name: string;
  task_type: ManagementTaskType;

  frequency: ManagementTaskFrequency;
  frequency_count?: number;
  frequency_unit?: ManagementTaskFrequencyUnit;

  description?: string;
  related_laws?: string[];
  contractor?: string;

  target_areas: ManagementTaskTargetArea[];
  linked_units: LinkedUnit[];

  attachments?: ManagementTaskAttachment[];
  remarks?: string;

  custom_fields?: ManagementTaskCustomFieldValue[];

  /** 法定点検フラグ。task_type === "点検" のときのみ意味を持つ */
  is_legal_inspection?: boolean;

  created_at: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface CreateManagementTaskBody {
  property_id: string;
  name: string;
  task_type: ManagementTaskType;
  frequency: ManagementTaskFrequency;
  frequency_count?: number;
  frequency_unit?: ManagementTaskFrequencyUnit;
  description?: string;
  related_laws?: string[];
  contractor?: string;
  target_areas?: ManagementTaskTargetArea[];
  linked_units?: LinkedUnit[];
  attachments?: ManagementTaskAttachment[];
  remarks?: string;
  custom_fields?: ManagementTaskCustomFieldValue[];
  is_legal_inspection?: boolean;
}

export interface UpdateManagementTaskBody {
  name?: string;
  task_type?: ManagementTaskType;
  frequency?: ManagementTaskFrequency;
  frequency_count?: number;
  frequency_unit?: ManagementTaskFrequencyUnit;
  description?: string;
  related_laws?: string[];
  contractor?: string;
  target_areas?: ManagementTaskTargetArea[];
  linked_units?: LinkedUnit[];
  attachments?: ManagementTaskAttachment[];
  remarks?: string;
  custom_fields?: ManagementTaskCustomFieldValue[];
  no?: number;
  is_legal_inspection?: boolean;
}

interface ManagementTaskState {
  list: {
    data: ManagementTaskRecord[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: ManagementTaskState = {
  list: { data: [], loading: false, error: null, loaded: false },
};

export const fetchManagementTasks = createAsyncThunk<
  ManagementTaskRecord[],
  {
    property_id: string;
    task_type?: ManagementTaskType;
    keyword?: string;
  }
>("managementTasks/fetch", async (params, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/management-tasks`, {
      params,
    });
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("管理業務の取得に失敗しました");
  }
});

export const fetchManagementTaskById = createAsyncThunk<
  ManagementTaskRecord,
  { property_id: string; task_id: string }
>(
  "managementTasks/fetchById",
  async ({ property_id, task_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(
        `${BaseURL}/api/v1/management-tasks/${property_id}/${task_id}`,
      );
      return res.data.data;
    } catch {
      return rejectWithValue("管理業務の取得に失敗しました");
    }
  },
);

export const createManagementTask = createAsyncThunk<
  ManagementTaskRecord,
  CreateManagementTaskBody
>("managementTasks/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/management-tasks`,
      body,
    );
    return res.data.data;
  } catch (e) {
    const msg =
      (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message || "管理業務の登録に失敗しました";
    return rejectWithValue(msg);
  }
});

export const updateManagementTask = createAsyncThunk<
  ManagementTaskRecord,
  { property_id: string; task_id: string; body: UpdateManagementTaskBody }
>(
  "managementTasks/update",
  async ({ property_id, task_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/management-tasks/${property_id}/${task_id}`,
        body,
      );
      return res.data.data;
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "管理業務の更新に失敗しました";
      return rejectWithValue(msg);
    }
  },
);

export const deleteManagementTask = createAsyncThunk<
  { property_id: string; task_id: string },
  { property_id: string; task_id: string }
>("managementTasks/delete", async (params, { rejectWithValue }) => {
  try {
    await ipponApi.delete(
      `${BaseURL}/api/v1/management-tasks/${params.property_id}/${params.task_id}`,
    );
    return params;
  } catch {
    return rejectWithValue("管理業務の削除に失敗しました");
  }
});

const managementTaskSlice = createSlice({
  name: "managementTasks",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchManagementTasks.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchManagementTasks.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchManagementTasks.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = (action.payload as string) || null;
      })
      .addCase(createManagementTask.fulfilled, (state, action) => {
        state.list.data.push(action.payload);
      })
      .addCase(updateManagementTask.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (t) => t.task_id === action.payload.task_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
        else state.list.data.push(action.payload);
      })
      .addCase(deleteManagementTask.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (t) => t.task_id !== action.payload.task_id,
        );
      });
  },
});

export default managementTaskSlice.reducer;
